import React, { useEffect, useMemo, useState } from "react";
import "./LearnersReport.css";
import axios from "axios";
import { BASE_API_URL } from "../../utils/constants";
import {
  CircularProgress,
  Alert,
} from "@mui/material";

import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  TextField,
  MenuItem,
  InputAdornment,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  AreaChart,
  Area,
  Cell,
  Tooltip,
  Legend,
  CartesianGrid,
  XAxis,
  YAxis,
} from "recharts";

import { DataGrid } from "@mui/x-data-grid";

const LearnersReport = () => {
  const [reportData, setReportData] = useState(null);

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");

  const [search, setSearch] = useState("");

  const [level, setLevel] = useState("all");

  const [selectedHub, setSelectedHub] = useState("Core Hub");

  const today = new Date().toLocaleDateString("en-GB", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

  const [paginationModel, setPaginationModel] = useState({
  page: 0,
  pageSize: 10,
});

  const hubs = [
  "Core Hub",
  "Product Hub",
  "Sales & Solutioning Hub",
  "Future of Experience Hub",
  "Digital Customer .Next Hub",
  "Delivery Modernization Hub",
  "GenAI Agentic Hub",
];  


 useEffect(() => {
  const fetchReport = async () => {
    try {
      setLoading(true);
      setError("");

      const res = await axios.get(
        `${BASE_API_URL}/powerBI/foundation-report`,
         {
        params: {
            hub: selectedHub,
        },
    }
      );

      setReportData(res.data?.[0]);
      console.log("data1", res.data?.[0]);
    } catch (err) {
      setError("Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  fetchReport();
}, [selectedHub]);

 const level8Above = reportData?.Song?.summary?.level8Above || {};
const level9To11 = reportData?.Song?.summary?.level9To11 || {};

const foundationCompleted =
  (level8Above.foundationCompletionCount || 0) +
  (level9To11.foundationCompletionCount || 0);

const foundationTotal =
  (level8Above.totalLearners || 0) +
  (level9To11.totalLearners || 0);

const foundationPct =
  foundationTotal
    ? ((foundationCompleted / foundationTotal) * 100).toFixed(2)
    : 0;

const flagshipCompleted =
  level8Above.flagshipCompletionCount || 0;

const flagshipTotal =
  level8Above.totalLearners || 0;

const flagshipPct =
  level8Above.flagshipCompletionPct || 0;

  const workbook1 =
    reportData?.Song?.workbook1 || [];

  const workbook2 =
    reportData?.Song?.workbook2 || [];

  const tableRows = useMemo(() => {
    let rows = [];

    if (level === "all")
      rows = [...workbook1, ...workbook2];

    else if (level === "8")
      rows = workbook1;

    else rows = workbook2;

    return rows
      .filter((item) =>
        item.entity
          ?.toLowerCase()
          .includes(search.toLowerCase())
      )
      .map((item, index) => ({
        id: index + 1,
        sn: index + 1,
        ...item,
      }));
  }, [workbook1, workbook2, search, level]);

  const totalUsers = useMemo(() => {
  return tableRows.reduce(
    (sum, row) => sum + (Number(row.totalLearners) || 0),
    0
  );
}, [tableRows]);

  const totalPages = Math.ceil(
  tableRows.length / paginationModel.pageSize
);

const currentPage = paginationModel.page + 1;

const visiblePages = Array.from(
  { length: totalPages },
  (_, i) => i + 1
).filter(
  (page) =>
    page === 1 ||
    page === totalPages ||
    Math.abs(page - currentPage) <= 2
);

  const columns = [
  { field: "sn", headerName: "S.N", flex: 0.5, minWidth: 70 },
  { field: "entity", headerName: "Entity", flex: 1.5, minWidth: 180 },
  { field: "owner", headerName: "Owner", flex: 1.4, minWidth: 180 },
  { field: "totalLearners", headerName: "Total Learners", flex: 1, minWidth: 140 },
  { field: "completed", headerName: "Completed", flex: 1, minWidth: 120 },
  { field: "inProgress", headerName: "In Progress", flex: 1, minWidth: 120 },
  { field: "notStarted", headerName: "Not Started", flex: 1, minWidth: 120 },
];

  const summaryCards = [
  {
    title: "Foundation",
    value: foundationCompleted,
    percent: foundationPct,
    total: foundationTotal,
    gradient: "linear-gradient(90deg, #4D5FAA, #5773EF)",
  },
  {
    title: "Flagship",
    value: flagshipCompleted,
    percent: flagshipPct,
    total: flagshipTotal,
    gradient: "linear-gradient(90deg, #E09563, #FCA750)",
  },
];

  const weeklyChart = [
    {
    level: "SM & Above",
    value: reportData?.Song?.weeklyReport?.smAbove || 0,
  },
  {
    level: "Level 5-6",
    value: reportData?.Song?.weeklyReport?.level5To6 || 0,
  },
  {
    level: "Level 7-8",
    value: reportData?.Song?.weeklyReport?.level7To8 || 0,
  },
  {
    level: "Level 9-10",
    value: reportData?.Song?.weeklyReport?.level9To10 || 0,
  },
  {
    level: "Level 11",
    value: reportData?.Song?.weeklyReport?.level11 || 0,
  },
];

  const pieData = useMemo(() => {
  const rows = [...workbook1, ...workbook2];

  const completed = rows.reduce(
    (sum, row) => sum + (row.completed || 0),
    0
  );

  const inProgress = rows.reduce(
    (sum, row) => sum + (row.inProgress || 0),
    0
  );

  const notStarted = rows.reduce(
    (sum, row) => sum + (row.notStarted || 0),
    0
  );

  const total = rows.reduce(
    (sum, row) => sum + (row.totalLearners || 0),
    0
  );

  return [
    {
      name: "Total Participant",
      value: total,
    },
    {
      name: "Completion Status",
      value: completed,
    },
    {
      name: "In Progress",
      value: inProgress,
    },
    {
      name: "Not Started",
      value: notStarted,
    },
  ];
}, [workbook1, workbook2]);

  const lineData = [
    { week: "Week 1", users: 120 },
    { week: "Week 2", users: 80 },
    { week: "Week 3", users: 300 },
    { week: "Week 4", users: 450 },
    { week: "Week 5", users: 560 },
    { week: "Week 6", users: 620 },
    { week: "Week 7", users: 540 },
    { week: "Week 8", users: 450 },
    { week: "Week 9", users: 300 },
    { week: "Week 10", users: 180 },
  ];

  const weeklyColors = [
    "#05F2DB",
    "#EAAC01",
    "#FB6E02",
    "#A100FF",
    "#D9D9D9",
  ];

  const pieColors = [
    "#A100FF",
    "#EAAC01",
    "#FB6E02",
    "#00EFD1",
  ];

  const activityAreaFill = "rgba(91,110,225,0.16)";

 if (loading)
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "70vh",
        gap: 2,
      }}
    >
      <CircularProgress size={55} thickness={4} />
      <Typography variant="h6" color="text.secondary">
        Loading Learners Report...
      </Typography>
    </Box>
  );
 if (error)
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "70vh",
      }}
    >
      <Alert
        severity="error"
        sx={{
          width: 420,
          fontSize: "16px",
        }}
      >
        {error}
      </Alert>
    </Box>
  );

  return (
  <div className="learnersLayout">


   <div className="leftSidebar">

  {hubs.map((hub) => (
    <div
      key={hub}
      className={
        selectedHub === hub
          ? "sidebarItem active"
          : "sidebarItem"
      }
      onClick={() => setSelectedHub(hub)}
    >
      {hub}
    </div>
  ))}

</div>


    <div className="dashboardContainer">
     {selectedHub === "Core Hub" ? (
      <>

      <Typography
        variant="h5"
        className="sectionTitle"
      >
        Overall Learning Completion Summery
      </Typography>


      <div className="summaryGrid">
  {summaryCards.map((card) => (
    <Card
      key={card.title}
      className="summaryCard"
      sx={{
        background: card.gradient
      }}
    >
      <CardContent className="summaryCardContent">

     <div className="summaryCardLeft">

  <div className="summaryIconWrapper">
    <img
      src={`${process.env.PUBLIC_URL}/Assets/foundation-icon.svg`}
      alt={card.title}
      className="summary-card-icon"
    />
  </div>

  <Typography className="cardTitle">
    {card.title}
  </Typography>

</div>

        <div className="summaryCardRight">
          <Typography className="cardNumber">
            {card.value}
          </Typography>

          <Typography className="cardSubtext">
            Total completion out of {card.total} Participants
          </Typography>

          <Typography className="cardPercent">
            {card.percent}%
          </Typography>
        </div>

      </CardContent>
    </Card>
  ))}
</div>

      <div className="chartSection">
        <div className="chartSectionLeft">
          <Typography className="chartSectionTitle" variant="h6"><strong>Learning Completion by Level ({today})</strong></Typography>
          <Paper className="chartCard weeklyChartCard">
            <ResponsiveContainer
              width="100%"
              height={250}
            >
              <BarChart data={weeklyChart} margin={{ top: 16, right: 16, left: 16, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="rgba(255,255,255,0.16)" strokeDasharray="3 3" />
                <XAxis
                dataKey="level"
                axisLine={false}
                tickLine={false}
                tick={false}
/>
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#ffffffcc', fontSize: 12 }}
                />
                <Tooltip
                  cursor={false}
                  contentStyle={{
                    borderRadius: 10,
                    border: 'none',
                    boxShadow: '0 10px 30px rgba(0,0,0,.12)',
                  }}
                />
                <Bar dataKey="value"  barSize={25} radius={[4, 4, 0, 0]}  barCategoryGap="40%">
                  {weeklyChart.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={weeklyColors[index % weeklyColors.length]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="chartLegendBar">
              {weeklyChart.map((item, index) => (
                <div key={item.level} className="legendItem">
                  <span
                    className="legendDot"
                    style={{ background: weeklyColors[index % weeklyColors.length] }}
                  />
                  <span>{item.level}</span>
                </div>
              ))}
            </div>
          </Paper>
        </div>
        <div className="chartSectionRight">
          <Typography className="chartSectionTitle" variant="h6"><strong>Overall Learning Completion Status</strong></Typography>
          <Paper className="chartCard overallStatusCard borderedCard">
            <div className="pieChartWrapper">
               <div className="pieChart">
              <ResponsiveContainer width="100%"height={250}>
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    cx="45%"
                    cy="50%"
                    outerRadius={70}
                    innerRadius={0}
                    labelLine={false}
                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                  >
                    {pieData.map((item, index) => (
                      <Cell
                        key={index}
                        fill={pieColors[index]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    cursor={false}
                    contentStyle={{
                      borderRadius: 10,
                      border: 'none',
                      boxShadow: '0 10px 30px rgba(0,0,0,.12)',
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              </div>
              <div className="pieLegend">
                {pieData.map((item, index) => (
                  <div key={item.name} className="pieLegendItem">
                    <span
                      className="legendDot"
                      style={{ background: pieColors[index] }}
                    />
                    <span>{item.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </Paper>
        </div>
      </div>


      {/* <Paper className="chartCard activityChart">
        <div className="activityHeader">
          <div>
            <div className="activityLabel">ACTIVE USERS</div>
            <Typography
              variant="h6"
              className="chartTitle"
            >
              Weekly Session Activity
            </Typography>
          </div>
          <div className="liveBadge">Live</div>
        </div>
        <ResponsiveContainer
          width="100%"
          height={250}
        >
          <AreaChart data={lineData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <XAxis
              dataKey="week"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6b7280', fontSize: 12 }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6b7280', fontSize: 12 }}
            />
            <Tooltip
              cursor={false}
              contentStyle={{
                borderRadius: 10,
                border: 'none',
                boxShadow: '0 10px 30px rgba(0,0,0,.12)',
              }}
            />
            <Area
              type="monotone"
              dataKey="users"
              stroke="#5b6ee1"
              strokeWidth={3}
              fill={activityAreaFill}
              dot={false}
              activeDot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Paper> */}


      <Paper className="tablePaper">

        <Box className="tableHeader">

<Box className="tableHeaderLeft">
          <Typography
            variant="h6"
            className="tableTitle"
          >
           Learner Completion Details
          </Typography>

          <Typography className="totalUsers"  variant="h6">
         <strong>Total Users :</strong>
         <span className="totalUsersCount">{totalUsers}</span>
         </Typography>
         </Box>

          <Box className="tableFilters">

            <TextField
              size="small"
              placeholder="Search Entity"
              value={search}
              onChange={(e)=>setSearch(e.target.value)}
              InputProps={{
                startAdornment:(
                  <InputAdornment position="start">
                    <SearchIcon/>
                  </InputAdornment>
                )
              }}
            />

            <TextField
              select
              size="small"
              value={level}
              onChange={(e)=>setLevel(e.target.value)}
              sx={{width:180}}
            >

              <MenuItem value="all">
                All Levels
              </MenuItem>

              <MenuItem value="8">
                Level 8 & Above
              </MenuItem>

              <MenuItem value="9">
                Level 9 - 11
              </MenuItem>

            </TextField>

          </Box>

        </Box>

        <div
          style={{
          width: "100%",
          height: 650,
  }}
        >

          <DataGrid

            rows={tableRows}

            columns={columns}

            pageSizeOptions={[10,20,50]}

            disableRowSelectionOnClick

            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            hideFooter

            rowHeight={52}
            density="standard"

            sx={{

              border:0,

              "& .MuiDataGrid-columnHeaders":{
                background:"#5b2ca0",
                color:"#fff",
                fontWeight:700
              },

              "& .MuiDataGrid-row:hover":{
                background:"#f5f5ff"
              },

            }}

          />

        </div>

      </Paper>

      <div className="customPagination">
         <div className="pageNumbers">

    <button
      disabled={paginationModel.page === 0}
      onClick={() =>
        setPaginationModel((prev) => ({
          ...prev,
          page: prev.page - 1,
        }))
      }
    >
      &lt;
    </button>

    {visiblePages.map((page) => (
      <button
        key={page}
        className={page === currentPage ? "activePage" : ""}
        onClick={() =>
          setPaginationModel((prev) => ({
            ...prev,
            page: page - 1,
          }))
        }
      >
        {page}
      </button>
    ))}

    <button
      disabled={paginationModel.page === totalPages - 1}
      onClick={() =>
        setPaginationModel((prev) => ({
          ...prev,
          page: prev.page + 1,
        }))
      }
    >
      &gt;
    </button>

          </div>

          <div className="itemsPerPage">

    <span>Items per page</span>

    <select
      value={paginationModel.pageSize}
      onChange={(e) =>
        setPaginationModel({
          page: 0,
          pageSize: Number(e.target.value),
        })
      }
    >
      <option value={10}>10</option>
      <option value={20}>20</option>
      <option value={50}>50</option>
    </select>

           </div>
           </div>

       </>
        ) : (

    <div className="comingSoon">

      <Typography variant="h4">
        {selectedHub}
      </Typography>

      <Typography variant="h6">
        Coming Soon
      </Typography>

    </div>

  )}

  </div>
  </div>

);

};

export default LearnersReport;
